import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { FabricTypeHeader } from "@/components/materials/FabricTypeHeader";
import { toast } from "sonner";
import { MaterialForm, ACCESSORY_TYPES } from "@/components/materials/MaterialForm";
import { MaterialGroup } from "@/components/materials/MaterialGroup";
import { ListsDialog } from "@/components/materials/ListsDialog";
import { FabricFilters, EMPTY_FILTERS, matchesFilters, ALL } from "@/components/materials/FabricFilters";
import { SeasonSelect } from "@/components/materials/SeasonSelect";

export { ACCESSORY_TYPES };

const CONFIG = {
  tessuti: {
    noun: "tessuto", empty: "Nessun tessuto inserito.",
    blank: { name: "", type: "tessuto", unit: "metri", fabric_type: "", composition: "", color: "", height_cm: "", quantity: "", cost_per_unit: "", supplier: "", supplier_code: "", fusible: false },
    columns: ["fabric_types", "colors", "suppliers"],
  },
  accessori: {
    noun: "accessorio", empty: "Nessun accessorio inserito.",
    blank: { name: "", type: "", unit: "pezzi", color: "", quantity: "", cost_per_unit: "", supplier: "", supplier_code: "", covered: false, covered_fabric_id: "", buttons_per_meter: "" },
    columns: ["accessory_types", "colors", "suppliers"],
  },
};

const toNum = (v) => Number(v) || 0;
const NO_TYPE = "Senza tipo";
const TITLES = { new: (n) => `Nuovo ${n}`, variant: () => "Nuova variante", group: (n) => `Modifica ${n}` };

export const MaterialsSection = ({ kind, materials }) => {
  const cfg = CONFIG[kind];
  const fabric = kind === "tessuti";
  const qc = useQueryClient();
  const [dlg, setDlg] = useState(null); // { mode, form, group }
  const { data: lists = { fabric_types: [], colors: [], suppliers: [], accessory_types: [] }, isSuccess: listsReady } = useQuery({ queryKey: ["material-lists"], queryFn: async () => (await api.get("/materials/lists")).data });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const { data: seasons } = useQuery({ queryKey: ["material-seasons"], queryFn: async () => (await api.get("/materials/seasons")).data, enabled: fabric });
  const [seasonPick, setSeason] = useState(null);
  const season = seasonPick || seasons?.current || ALL;
  const shown = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    const hay = (m) => [m.name, m.color, m.supplier, m.supplier_code, m.type, m.fabric_type, m.composition].filter(Boolean).join(" ").toLowerCase();
    return materials.filter((m) => (!fabric || (matchesFilters(m, filters) && (season === ALL || m.season === season))) && words.every((w) => hay(m).includes(w)));
  }, [materials, filters, fabric, search, season]);
  const seasonCount = fabric && season !== ALL ? materials.filter((m) => m.season === season).length : materials.length;
  const groups = useMemo(() => {
    const m = new Map();
    shown.forEach((x) => { const k = fabric && season === ALL ? `${x.name}|${x.season}` : x.name; if (!m.has(k)) m.set(k, []); m.get(k).push(x); });
    return [...m.values()].map((variants) => ({ name: variants[0].name, season: fabric ? variants[0].season : undefined, variants }));
  }, [shown, fabric, season]);
  const byType = useMemo(() => {
    const m = new Map();
    groups.forEach((g) => { const t = g.variants[0].fabric_type || NO_TYPE; if (!m.has(t)) m.set(t, []); m.get(t).push(g); });
    return [...m.entries()].sort(([a], [b]) => (a === NO_TYPE) - (b === NO_TYPE) || a.localeCompare(b));
  }, [groups]);
  const filtered = shown.length !== seasonCount;
  const done = (msg) => { qc.invalidateQueries({ queryKey: ["materials"] }); toast.success(msg); setDlg(null); };
  const create = useMutation({ mutationFn: async (p) => (await api.post("/materials", p)).data, onSuccess: () => done(dlg?.mode === "variant" ? "Variante aggiunta" : "Salvato") });
  const editGroup = useMutation({
    mutationFn: async ({ patches, ...p }) => {
      const r = (await api.patch("/materials/group", p)).data;
      await Promise.all(patches.map(([id, body]) => api.patch(`/materials/${id}`, body)));
      return r;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); done("Modifiche salvate su tutte le varianti"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const openVariant = (g) => {
    const f = [...g.variants].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))[0];
    setDlg({ mode: "variant", group: g, form: { ...cfg.blank, season: newSeason, name: g.name, type: f.type, unit: f.unit, fabric_type: f.fabric_type || "", composition: f.composition || "", height_cm: f.height_cm ?? "", fusible: !!f.fusible, covered: !!f.covered, buttons_per_meter: f.buttons_per_meter ?? "",
      supplier: f.supplier || "", cost_per_unit: f.cost_per_unit ? String(f.cost_per_unit) : "" } });
  };
  const openEdit = (g) => setDlg({ mode: "group", group: g, form: { name: g.name, type: g.variants[0].type || "", supplier: new Set(g.variants.map((v) => v.supplier)).size === 1 ? g.variants[0].supplier || "" : "", mixedSuppliers: new Set(g.variants.map((v) => v.supplier)).size > 1, fabric_type: g.variants[0].fabric_type || "", composition: g.variants[0].composition || "", height_cm: g.variants[0].height_cm ?? "", fusible: g.variants.some((v) => v.fusible), covered: g.variants.some((v) => v.covered), buttons_per_meter: g.variants.find((v) => v.buttons_per_meter)?.buttons_per_meter ?? "", covers: Object.fromEntries(g.variants.map((v) => [v.id, v.covered_fabric_id || ""])), variants: g.variants, prices: Object.fromEntries(g.variants.map((v) => [v.id, String(v.cost_per_unit ?? 0)])) } });
  const newSeason = fabric ? (season !== ALL ? season : seasons?.current) : undefined;
  const save = () => {
    const f = dlg.form;
    if (!f.name.trim()) { toast.error("Nome obbligatorio"); return false; }
    if (fabric && dlg.mode !== "variant" && !f.fabric_type) { toast.error("Scegli la tipologia di tessuto"); return false; }
    if (!fabric && dlg.mode === "new" && !f.type) { toast.error("Scegli il tipo di accessorio"); return false; }
    if (fabric && dlg.mode !== "group" && !f.color) { toast.error("Scegli il colore"); return false; }
    if (dlg.mode !== "group" && !f.supplier) { toast.error("Scegli il fornitore"); return false; }
    const covers = !fabric && !!f.covered && /bottone/i.test(f.type || "");
    if (!fabric && dlg.mode !== "group" && covers && !f.covered_fabric_id) { toast.error("Scegli il tessuto con cui viene ricoperto"); return false; }
    const height = f.height_cm !== "" && f.height_cm != null ? toNum(f.height_cm) : null;
    if (dlg.mode === "group") {
      const patches = dlg.group.variants.map((v) => {
        const body = {};
        const c = toNum(String(f.prices[v.id] ?? "").replace(",", "."));
        if (c !== (v.cost_per_unit || 0)) body.cost_per_unit = c;
        if (!fabric && covers && (f.covers[v.id] || "") !== (v.covered_fabric_id || "")) body.covered_fabric_id = f.covers[v.id] || "";
        return [v.id, body];
      }).filter(([, b]) => Object.keys(b).length);
      return editGroup.mutateAsync({ patches, old_name: dlg.group.name, season: dlg.group.season, name: f.name.trim(), fabric_type: f.fabric_type || null, composition: f.composition || null, height_cm: height, ...(fabric ? { fusible: !!f.fusible } : { type: f.type || null, supplier: f.supplier || null, covered: covers, ...(covers && toNum(f.buttons_per_meter) > 0 ? { buttons_per_meter: toNum(f.buttons_per_meter) } : {}) }) });
    }
    if (dlg.mode === "new" && materials.some((g) => (!fabric || season === ALL || g.season === f.season) && g.name.toLowerCase() === f.name.trim().toLowerCase())) { toast.error(`Esiste già: aggiungi una variante a "${f.name.trim()}"`); return false; }
    return create.mutateAsync({ ...f, buttons_per_meter: covers && toNum(f.buttons_per_meter) > 0 ? toNum(f.buttons_per_meter) : null, covered: covers, covered_fabric_id: covers ? f.covered_fabric_id : null, name: f.name.trim(), quantity: toNum(f.quantity), min_stock: 0, cost_per_unit: toNum(f.cost_per_unit), height_cm: height });
  };
  const pending = create.isPending || editGroup.isPending;

  return (
    <div data-testid={`materials-${kind}`}>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:max-w-md sm:flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" placeholder={fabric ? "Cerca tipo di tessuto, nome del fornitore, colore, fornitore, codice colore..." : `Cerca ${cfg.noun}, colore, fornitore, codice colore...`} data-testid={`materials-${kind}-search`} />
        </div>
        {fabric && <SeasonSelect value={season} options={seasons?.options || []} current={seasons?.current} onChange={setSeason} />}
        <div className="ml-auto flex gap-2">
          <ListsDialog lists={lists} ready={listsReady} columns={cfg.columns} />
          <Button onClick={() => setDlg({ mode: "new", form: { ...cfg.blank, season: newSeason } })} disabled={fabric && !seasons} data-testid={`new-${kind}-btn`}><Plus className="mr-2 h-4 w-4" />{TITLES.new(cfg.noun)}</Button>
        </div>
      </div>
      {fabric && materials.length > 0 && <FabricFilters materials={materials} filters={filters} onChange={setFilters} />}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent onSave={async () => { if ((await save()) === false) throw new Error("incompleto"); }}>
          <DialogHeader><DialogTitle>{dlg && TITLES[dlg.mode](cfg.noun)}{dlg?.mode === "variant" && ` · ${dlg.group.name}`}</DialogTitle></DialogHeader>
          {dlg && <MaterialForm kind={kind} mode={dlg.mode} form={dlg.form} lists={lists} seasons={seasons?.options || []} set={(form) => setDlg({ ...dlg, form })} />}
          <DialogFooter>
            <Button onClick={() => Promise.resolve(save()).catch(() => {})} disabled={pending} data-testid={`save-${kind}-btn`}>{pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {fabric && !seasons ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{fabric ? "Tessuto (nome fornitore) / colore" : "Accessorio / colore"}</TableHead>
              <TableHead>Varianti / fornitore</TableHead>
              <TableHead className="text-right">{fabric ? "Costo/m" : "Costo"}</TableHead>
              <TableHead className="text-center">Giacenza</TableHead>
              <TableHead className="text-center">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fabric
              ? byType.map(([t, gs]) => [<FabricTypeHeader key={`t-${t}`} type={t} groups={gs} />,
                ...gs.map((g) => <MaterialGroup key={`${g.name}-${g.season}-${filtered}`} group={g} fabric={fabric} kind={kind} defaultOpen={filtered} onAddVariant={openVariant} onEdit={openEdit} seasons={seasons?.options} showSeason={season === ALL} />)])
              : groups.map((g) => <MaterialGroup key={`${g.name}-${filtered}`} group={g} fabric={fabric} kind={kind} defaultOpen={filtered} onAddVariant={openVariant} onEdit={openEdit} />)}
            {groups.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground" data-testid={`${kind}-empty`}>{filtered ? `Nessun ${cfg.noun} corrisponde alla ricerca o ai filtri.` : fabric && season !== ALL ? `Nessun tessuto per la stagione ${season}.` : cfg.empty}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      )}
    </div>
  );
};
